import React from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { gql } from '@/modes/waterSupplier/gql';
import { ResultOf } from 'gql.tada';
import { AsyncSelect } from '../ReactSelect';
import { Flex, Box, Text } from 'rebass';

export const GET_WATER_HAZARD_SURVEY_TEMPLATES = gql(`
  query GetWaterHazardSurveyTemplates {
    waterHazardSurveyTemplates {
      id
      name
      description
      instructions
      otherHazardsQuestion
      waterSupplierId
      items {
        waterHazardId
        displayOrder
      }
    }
  }
`);

export type HazardSurveyTemplate = ResultOf<
  typeof GET_WATER_HAZARD_SURVEY_TEMPLATES
>['waterHazardSurveyTemplates'][number];

type HazardSurveyTemplatePickerProps = {
  onChange: (template: HazardSurveyTemplate | null) => void;
  placeholder?: string;
};

type TemplateOption = HazardSurveyTemplate & { __group?: string };

const HazardSurveyTemplatePicker: React.FC<HazardSurveyTemplatePickerProps> = ({
  onChange,
  placeholder = 'Select template',
}) => {
  const client = useApolloClient();

  const loadOptions = async (inputValue: string) => {
    const { data } = await client.query({
      query: GET_WATER_HAZARD_SURVEY_TEMPLATES,
    });

    if (!data?.waterHazardSurveyTemplates) return [];

    const templates = data.waterHazardSurveyTemplates.filter((t) => {
      if (!inputValue) return true;
      return t.name.toLowerCase().includes(inputValue.toLowerCase());
    });

    const global = templates.filter((t) => !t.waterSupplierId);
    const company = templates.filter((t) => !!t.waterSupplierId);

    const groups: Array<{ label: string; options: HazardSurveyTemplate[] }> =
      [];

    if (company.length > 0) {
      groups.push({ label: 'Saved Templates', options: company });
    }
    if (global.length > 0) {
      groups.push({ label: 'System Templates', options: global });
    }

    // If no groups, return flat list
    return groups.length > 0 ? groups : templates;
  };

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      loadOptions={loadOptions}
      onChange={onChange}
      getOptionLabel={(option: HazardSurveyTemplate) => option.name}
      getOptionValue={(option: HazardSurveyTemplate) => option.id}
      placeholder={placeholder}
      isClearable
      formatOptionLabel={(option: HazardSurveyTemplate) => (
        <Flex flexDirection="column">
          <Text fontWeight="bold">{option.name}</Text>
          {option.description && (
            <Text
              fontSize={0}
              color="gray"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {option.description}
            </Text>
          )}
        </Flex>
      )}
    />
  );
};

export default HazardSurveyTemplatePicker;
